/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * Which origins are the Deskshare shop.
 *
 * Every environment's sign-in page must send a customer back to the shop page
 * that sent them, and hand that shop a sign-in token. Matching only the one
 * address in VITE_REACT_APP_SHOPPING_CART_URL dropped every customer of any
 * other shop host: sign-in started from https://feature.shop.deskshare.com
 * landed on the website's home page, by password, code and Google alike.
 *
 * A shop origin is:
 *   1. the configured shop address's origin, whatever it is; or
 *   2. https://shop.deskshare.com, or https://<one label>.shop.deskshare.com
 *      (feature.shop.deskshare.com, test.shop.deskshare.com, ...), on the
 *      default port. Those names exist only in Deskshare's own DNS.
 *
 * What must still never count as the shop, whatever else changes here:
 *   - http:// (a token must not travel unencrypted);
 *   - any other port;
 *   - any host that merely contains the name: shop.deskshare.com.example.com,
 *     evilshop.deskshare.com, shop-deskshare.com;
 *   - more than one label in front (a.b.shop.deskshare.com);
 *   - an address that fails to parse.
 * A token minted for a shop origin is still refused by the back end unless
 * that origin is in its ALLOWED_JWT_AUDIENCES, so this list widens where a
 * customer may be RETURNED, not which services accept a token.
 */

import { configuration } from "@/lib/configuration";

/** https://shop.deskshare.com or https://<label>.shop.deskshare.com, nothing else. */
const DESKSHARE_SHOP_ORIGIN =
  /^https:\/\/(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)?shop\.deskshare\.com$/;

/** True when `origin` (scheme://host[:port], as URL.origin gives it) is a Deskshare shop. */
export function isShopOrigin(origin: string): boolean {
  if (!origin || origin === "null") return false;
  try {
    if (origin === new URL(configuration.shoppingCartUrl).origin) return true;
  } catch {
    /* shop address unset or malformed here - the fixed pattern still applies */
  }
  return DESKSHARE_SHOP_ORIGIN.test(origin);
}
